import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import {
  BakeasyChipDelete,
  BakeasyChipNew,
  BakeasyChipNormal,
  BakeasyChipRead,
  BakeasyChipViewReply,
  BakeasyChipSent,
  BakeasyChipUrgent,
} from '../../components/common/BakeasyChip';
import * as Button from '../../components/buttons/BakeasyButtons';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { useNavigate } from 'react-router-dom';
import { formatMessageDateTime } from '../../utils/DateUtils';
import { authActions } from '../../redux_store/slices/authSlice';
import {
  MESSAGE_LEGENDS_COLORS,
  UNAUTHORIZED,
} from '../../constants/Constants';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import {
  deleteMessageFromList,
  fetchMessages,
  fetchNewMessageCount,
  messagesState,
  sendMessages,
  updateMessageStatus,
} from '../../redux_store/slices/messagesSlice';
import { t } from 'i18next';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { messageTableHeaders } from '../../constants/TableHeaders';
import { deleteKeyFromObject, preparePayload } from '../../utils/commonUtils';
import BakeasyLegendRow from '../../components/common/BakeasyLegendRow';
import { getProfileState } from '../../redux_store/slices/GetUserProfileSlice';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Messages: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();
  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);

  const [search, setSearch] = useState<string>('');
  const [messageList, setMessageList] = useState<any[]>([]);
  const [count, setCount] = React.useState<number>(0);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, value, rowsPerPage, pageNo, filter } =
    useAppSelector(messagesState);
  let _userProfile: any = useAppSelector(getProfileState);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  const handlReply = async (message: any, msgTo: any) => {
    await handleOk(message);
    navigate('/messagesThread', {
      state: {
        messageStatus: message?.status?.toLowerCase(),
        _id: message?._id,
        disabled: message?.to?.length > 1,
        to: msgTo,
      },
    });
  };

  const handleOk = async (message: any) => {
    const messageStatus = ['new', 'sent'];
    if (messageStatus.includes(message?.status?.toLowerCase())) {
      await dispatch(
        updateMessageStatus({
          messageId: message?._id,
          status: message?.status,
        })
      );
    }
    dispatch(fetchNewMessageCount(''));
    refreshPageData(pageNo - 1);
  };

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((message: any) => {
        let msgTo: any = [];
        msgTo = message?.to?.filter((item: any) => {
          return item === _userProfile?.value?.name;
        });

        if (
          msgTo?.length === 0 ||
          message?.from === _userProfile?.value?.name
        ) {
          msgTo = message?.to;
        }

        return {
          id: message._id,
          supplierName: message.supplierName,
          messageDateTime: message?.createdDate
            ? formatMessageDateTime(message?.createdDate, true)
            : '',
          from: message?.from ?? '',
          to: msgTo,
          priority:
            message?.priority === 'normal' ? (
              <BakeasyChipNormal label="normal" />
            ) : (
              <BakeasyChipUrgent label="urgent" />
            ),
          subject: message?.subject ?? '',
          status: message?.status.toLowerCase(),
          recsent:
            message?.status?.toLowerCase() === 'sent' ? (
              <BakeasyChipSent />
            ) : message?.status?.toLowerCase() === 'read' ? (
              <BakeasyChipRead />
            ) : (
              <BakeasyChipNew />
            ),
          content: message?.content ?? '',
          actions: [
            <BakeasyChipViewReply
              label={message?.to?.length > 1 ? 'VIEW' : 'VIEW_REPLY'}
              onClick={(event: any) => {
                event.stopPropagation();
                handlReply(message, msgTo);
              }}
            />,
            <BakeasyChipDelete
              onClick={(event: any) => {
                event.stopPropagation();
                openDeleteConfirmDialog(message._id);
              }}
            />,
          ],
        };
      });

      setMessageList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  const refreshPageData = async (
    newPage: number,
    search?: string,
    filter?: {} | undefined
  ) => {
    let payload = await preparePayload(
      { limit: rowsPerPage },
      filter,
      search,
      newPage
    );
    await dispatch(fetchMessages(payload));
    // await fetchData(fetchMessages, payload);

    dispatch(fetchNewMessageCount(''));
  };

  function openMessageFilterConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'message-filter',
      open: true,
      title: t('MESSAGE_FILTERS'),
      data: filter,
      onNoClick: (event: any) => {},
      onYesClick: (event: any, param: string | undefined) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      },
    });
  }

  function openComposeMessageConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'message-compose',
      open: true,
      title: t('NEW_MESSAGE'),
      onYesClick: async (event: any, param: any) => {
        const payload = {
          // to: [param?.selectedName?.supplierEntityId]
          to: param?.selectedName?.supplierEntityId
            ? [param?.selectedName?.supplierEntityId]
            : [],
          subject: param.selectSubject,
          content: param.selectContent,
          priority: param.checkedPriority,
        };
        await dispatch(sendMessages(payload));
        refreshPageData(pageNo - 1);
      },
    });
  }

  function openDeleteConfirmDialog(id: number) {
    myContext.setConfirmDialog({
      type: 'message-delete',
      open: true,
      title: t('DELETE_MESSAGE'),
      message: t('DELETE_MESSAGE_CONFIRMATION'),
      onYesClick: (event: any, param: string | undefined) => {
        handleDeleteMessageRow(id);
      },
    });
  }

  async function handleDeleteMessageRow(id: number) {
    await dispatch(deleteMessageFromList({ messageId: id }));
    refreshPageData(pageNo - 1);
  }

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function messagesUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={'MESSAGES'} />

          <Grid
            container
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (
                    event?.target?.value?.length === 0 ||
                    event?.target?.value?.length > 2
                  ) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0
                        ? undefined
                        : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(
                  event: React.KeyboardEvent<HTMLInputElement>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <div
                className="tableOptions"
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                {/* <Button.Print
									onClick={function (
										event: React.MouseEvent<HTMLButtonElement, MouseEvent>
									): void {}}
								/> */}
                <Button.Filter
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    openMessageFilterConfirmDialog();
                  }}
                />

                <Button.NewMsg
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    openComposeMessageConfirmDialog();
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />
          <BakeasyLegendRow colorLabels={MESSAGE_LEGENDS_COLORS} />

          <Grid>
            {messageList?.length > 0 ? (
              <BakeasyTable
                pageNo={pageNo - 1}
                ref={tableRef}
                search={search}
                headers={messageTableHeaders}
                tableData={messageList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return messagesUI();
};
